// Piezas chicas de la app «Leads»: glifo, chip de etapa, selector «Mover a…».

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { ArrowRightLeft, Check, Users } from 'lucide-react';

import { STAGES, STAGE_BY_KEY, type StageKey } from '../model';

export function LeadsGlyph({ size = 36 }: { size?: number }) {
  return (
    <span aria-hidden style={{ width: size, height: size }} className="la-glyph grid shrink-0 place-items-center rounded-xl">
      <Users style={{ width: size * 0.52, height: size * 0.52 }} strokeWidth={2.2} />
    </span>
  );
}

export function StageChip({ status, className = '' }: { status: string; className?: string }) {
  const s = STAGE_BY_KEY[status];
  if (!s) return <span className={`inline-flex rounded-full bg-muted px-2 py-0.5 text-[11.5px] font-medium text-muted-foreground ${className}`}>{status || '—'}</span>;
  return (
    <span title={s.hint} className={`${s.tone} la-chip inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${className}`}>
      <span className="la-dot h-1.5 w-1.5 rounded-full" aria-hidden />
      {s.label}
    </span>
  );
}

/** Selector segmentado de etapa (radiogroup con flechas). */
export function StageSelector({ value, onChange, disabled, busy }: {
  value: string; onChange: (k: StageKey) => void; disabled?: boolean; busy?: boolean;
}) {
  const onKey = (e: KeyboardEvent) => {
    if (disabled) return;
    const i = Math.max(0, STAGES.findIndex((s) => s.key === value));
    const n = STAGES.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onChange(STAGES[(i + 1) % n].key); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onChange(STAGES[(i + n - 1) % n].key); }
  };
  return (
    <div role="radiogroup" aria-label="Etapa del lead" aria-busy={busy || undefined} onKeyDown={onKey}
      className={`grid grid-cols-3 gap-1 rounded-xl border border-border bg-muted p-1 min-[480px]:grid-cols-5 ${disabled ? 'opacity-70' : ''}`}>
      {STAGES.map((s) => {
        const sel = value === s.key;
        return (
          <button key={s.key} type="button" role="radio" aria-checked={sel} tabIndex={sel ? 0 : -1} disabled={disabled}
            onClick={() => !sel && onChange(s.key)} title={s.hint}
            className={[
              s.tone,
              'flex min-w-0 flex-col items-center gap-1 rounded-[10px] px-1 py-1.5 text-[11.5px] font-medium transition-colors',
              sel ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              disabled ? 'cursor-not-allowed' : '',
            ].join(' ')}>
            <span className={`la-dot h-2 w-2 rounded-full ${sel ? '' : 'opacity-60'}`} aria-hidden />
            <span className="max-w-full truncate">{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** «Mover a…»: alternativa accesible (teclado/lectores) al arrastre del embudo. */
export function MoveMenu({ current, onMove, label = 'Mover a…', compact }: {
  current: string; onMove: (k: StageKey) => void; label?: string; compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc, true);
    // Foco al primer destino al abrir.
    window.setTimeout(() => ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc, true); };
  }, [open]);
  const onMenuKey = (e: KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
    const i = items.findIndex((b) => b === document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
  };
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}
        title={label}
        className={`inline-flex items-center gap-1 rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground ${compact ? 'h-8 w-8 justify-center' : 'px-2 py-1 text-[12px]'}`}>
        <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
        {compact ? <span className="sr-only">{label}</span> : label}
      </button>
      {open && (
        <div id={id} role="menu" aria-label="Mover a la etapa" onKeyDown={onMenuKey}
          className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          {STAGES.map((s) => (
            <button key={s.key} type="button" role="menuitem" disabled={s.key === current}
              onClick={() => { setOpen(false); onMove(s.key); }}
              className={`${s.tone} flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-muted disabled:cursor-default disabled:opacity-60`}>
              <span className="la-dot h-2 w-2 rounded-full" aria-hidden />
              <span className="flex-1">{s.label}</span>
              {s.key === current && <Check className="h-3.5 w-3.5" aria-label="actual" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
